import { useUnits } from '../lib/preferences.js';

/** Count ZIPs with at least one rule in `category` at or above `minSeverity`. */
function countAtLeast(zips, category, minSeverity) {
  return zips.filter((z) => z.rules.some((r) => r.category === category && r.severity >= minSeverity)).length;
}

export default function SummaryStrip({ snap }) {
  const u = useUnits();
  const { counts, peakWetBulb, medianWetBulbF, zips } = snap;

  // Every zip in a county shares one state, so its jurisdiction map is
  // uniform across the whole array — the first entry is representative.
  const jurisdiction = zips[0]?.jurisdiction;

  const heatCritical = countAtLeast(zips, 'heat', 3);
  const heatAction = countAtLeast(zips, 'heat', 2);
  const smokeAction = countAtLeast(zips, 'air', 2);
  const windAction = countAtLeast(zips, 'wind', 2);

  // Outside the US no work rules are evaluated, so rule counts would read
  // as a misleading "0 critical" — show the environmental stats only.
  const rulesOn = zips[0]?.rulesEvaluated !== false;
  const pointWord = zips[0]?.kind === 'municipality' ? 'municipalities' : 'ZIPs';

  return (
    <div className="summary">
      {rulesOn && (
        <>
      <div className={`stat sev-${counts.critical ? 3 : 0}`}>
        <div className="label">Critical</div>
        <div className="value">{counts.critical}</div>
        <div className="note">ZIPs with a critical trigger</div>
      </div>
      <div className={`stat sev-${counts.action ? 2 : 0}`}>
        <div className="label">Action required</div>
        <div className="value">{counts.action}</div>
        <div className="note">ZIPs needing a control today</div>
      </div>
        </>
      )}
      <div className="stat">
        <div className="label">Peak wet bulb</div>
        <div className="value">{u.fmtTemp(peakWetBulb?.wetBulbF, 1)}</div>
        <div className="note">
          {peakWetBulb ? (rulesOn ? `${peakWetBulb.zip} · ${peakWetBulb.place}` : peakWetBulb.place) : '—'}
        </div>
      </div>
      <div className="stat">
        <div className="label">Median wet bulb</div>
        <div className="value">{u.fmtTemp(medianWetBulbF, 1)}</div>
        <div className="note">Across all monitored {pointWord}</div>
      </div>
      {rulesOn && (
        <>
      <div className={`stat sev-${heatCritical ? 3 : heatAction ? 2 : 0}`}>
        <div className="label">Heat</div>
        <div className="value">
          {heatCritical} <span style={{ fontSize: 15, color: 'var(--text-faint)' }}>/ {heatAction}</span>
        </div>
        <div className="note">
          {jurisdiction?.heat?.agency ?? '—'} · critical / action-required
        </div>
      </div>
      <div className={`stat sev-${smokeAction ? 2 : 0}`}>
        <div className="label">Wildfire smoke</div>
        <div className="value">{smokeAction}</div>
        <div className="note">{jurisdiction?.smoke?.agency ?? '—'} · action-required or worse</div>
      </div>
      {jurisdiction?.wind && (
        <div className={`stat sev-${windAction ? 2 : 0}`}>
          <div className="label">Wind</div>
          <div className="value">{windAction}</div>
          <div className="note">{jurisdiction.wind.agency ?? '—'} · action-required or worse</div>
        </div>
      )}
        </>
      )}
    </div>
  );
}
